import { useState, useEffect } from 'react'
import Button from '@/components/Button'
import { PageMetadata } from '@/types/page'
import { useWidget } from './shared/useWidget'
import { WidgetPreview } from './shared/WidgetPreview'
import { WidgetConfig } from './shared/types'

export const metadata: PageMetadata = {
  id: 'customization-4-get-config',
  category: 'customization',
  categoryName: 'Customization',
  name: 'Get Config',
  icon: '⚙️',
  path: '/customization/get-config',
  title: 'Zendesk Widget - Get Configuration',
  description: 'Retrieve current widget configuration using messenger:get API'
}

export default function GetConfig() {
  useWidget() // Initialize widget
  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig | null>(null)

  const fetchWidgetConfig = () => {
    if (!window.zE) {
      console.error('Zendesk widget not loaded')
      return
    }

    window.zE('messenger:get', 'config', (config: WidgetConfig) => {
      setWidgetConfig(config)
    })
  }

  useEffect(() => {
    document.title = `Internal Note - ${metadata.title}`

    // Fetch config on load after widget is ready
    const timer = setTimeout(() => {
      fetchWidgetConfig()
    }, 1000)

    return () => clearTimeout(timer)
  }, [])

  return (
    <>
      <header>
        <div className="py-6 px-4 sm:px-6 md:px-8 mt-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl text-licorice dark:text-white">{metadata.title}</h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                {metadata.description}
              </p>
            </div>
          </div>
        </div>
      </header>

      <div className="py-6 px-4 sm:px-6 lg:px-8 mb-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6 items-start">
          {/* Left column: Form - Scrollable */}
          <div className="max-h-[calc(100vh-200px)] overflow-y-auto pr-4">
            <section className="mb-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-licorice dark:text-white">Widget Configuration</h3>
                <Button onClick={fetchWidgetConfig} type="button">
                  Refresh Config
                </Button>
              </div>
            </section>

            <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-2">
                About messenger:get
              </h3>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                The <code className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900 rounded">messenger:get</code> API allows you to retrieve the current configuration of the widget at runtime, including multi-conversation settings and launcher shape.
              </p>
            </div>

            {widgetConfig ? (
              <div className="space-y-4">
                <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
                    Can User Create More Conversations
                  </h3>
                  <p className="text-lg font-medium text-licorice dark:text-white">
                    {widgetConfig.canUserCreateMoreConversations ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Whether the current user is permitted to create additional conversations
                  </p>
                </div>

                <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
                    Multi-Conversation Enabled
                  </h3>
                  <p className="text-lg font-medium text-licorice dark:text-white">
                    {widgetConfig.multiConvoEnabled ? 'Yes' : 'No'}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Whether multi-conversation mode is enabled for the widget
                  </p>
                </div>

                <div className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">
                    Launcher Shape
                  </h3>
                  <p className="text-lg font-medium text-licorice dark:text-white capitalize">
                    {widgetConfig.launcherShape}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    The shape of the widget launcher button
                  </p>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                    Raw Response
                  </h3>
                  <pre className="bg-gray-900 p-3 rounded-md overflow-x-auto text-xs text-gray-300">
                    {JSON.stringify(widgetConfig, null, 2)}
                  </pre>
                </div>

                <div className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-2">
                    Code Example
                  </h3>
                  <pre className="bg-gray-900 p-3 rounded-md overflow-x-auto text-xs text-gray-300">
{`zE('messenger:get', 'config', (config) => {
  console.log('Can create conversations:', config.canUserCreateMoreConversations);
  console.log('Multi-convo enabled:', config.multiConvoEnabled);
  console.log('Launcher shape:', config.launcherShape);
});`}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Click "Refresh Config" to fetch the current widget configuration
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  This will call <code className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">messenger:get</code> with the <code className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded text-xs">'config'</code> parameter
                </p>
              </div>
            )}
          </div>

          {/* Right column: Widget Preview - Fixed */}
          <WidgetPreview />
        </div>
      </div>
    </>
  )
}
